import { useEffect, useId, useState, type SyntheticEvent } from 'react';
import { Link } from 'react-router';

import {
  AuthField,
  authInputClass,
  AuthLayout,
  AuthNotice,
  AuthSubmit,
} from '../features/auth/AuthLayout';
import { confirmPasswordReset } from '../features/auth/session';
import { ApiError, NetworkError } from '../lib/api';

const MIN_LENGTH = 12;

/**
 * The token arrives after "#" in the e-mailed link, so it never reaches a
 * server log; it is removed from the address bar once read.
 */
function readToken(): string {
  return new URLSearchParams(window.location.hash.slice(1)).get('token') ?? '';
}

/** Sets a new password from a reset link (ADR-029). */
export function ResetPasswordPage() {
  const [token] = useState(readToken);
  useEffect(() => {
    if (window.location.hash) window.history.replaceState(null, '', window.location.pathname);
  }, []);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const errorId = useId();
  const mismatch = confirm !== '' && confirm !== password;

  const onSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    if (password.length < MIN_LENGTH) {
      setError(`A senha deve ter pelo menos ${String(MIN_LENGTH)} caracteres.`);
      return;
    }
    if (mismatch) {
      setError('As duas senhas não são iguais.');
      return;
    }
    setBusy(true);
    try {
      await confirmPasswordReset(token, password);
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError && err.fields.token) setExpired(true);
      else if (err instanceof ApiError && err.fields.new_password === 'too_long')
        setError('A senha é longa demais.');
      else if (err instanceof ApiError && err.fields.new_password)
        setError(`A senha deve ter pelo menos ${String(MIN_LENGTH)} caracteres.`);
      else if (err instanceof NetworkError)
        setError('Não foi possível contatar o servidor. Verifique sua conexão.');
      else setError('Ocorreu um erro inesperado. Tente novamente.');
    } finally {
      setBusy(false);
    }
  };

  if (!token || expired)
    return (
      <AuthLayout title="Link inválido" subtitle="Este link não pode mais ser usado.">
        <div className="mt-5 space-y-4">
          <AuthNotice tone="error">
            O link para criar uma nova senha expirou, já foi usado ou está incompleto. Os links
            valem por 1 hora e só uma vez.
          </AuthNotice>
          <p className="text-center text-[13px]">
            <Link to="/forgot-password" className="text-accent hover:underline">
              Pedir um novo link
            </Link>
          </p>
        </div>
      </AuthLayout>
    );

  if (done)
    return (
      <AuthLayout title="Senha alterada" subtitle="Você já pode entrar com a nova senha.">
        <div className="mt-5 space-y-4">
          <AuthNotice tone="success">
            Sua senha foi alterada. Por segurança, você saiu do sistema em todos os aparelhos.
          </AuthNotice>
          <Link
            to="/login"
            className="inline-flex h-9 w-full items-center justify-center rounded-md bg-accent-strong text-[13px] font-medium text-white hover:bg-accent"
          >
            Ir para o login
          </Link>
        </div>
      </AuthLayout>
    );

  return (
    <AuthLayout
      title="Criar nova senha"
      subtitle="Escolha a senha que você vai usar a partir de agora."
    >
      <form
        className="mt-5 space-y-4"
        onSubmit={(e) => {
          void onSubmit(e);
        }}
        noValidate
      >
        <AuthField
          label="Nova senha"
          htmlFor="new-password"
          hint={`Pelo menos ${String(MIN_LENGTH)} caracteres. Uma frase é mais fácil de lembrar.`}
        >
          <input
            id="new-password"
            type="password"
            autoComplete="new-password"
            autoFocus
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
            }}
            aria-describedby={error ? errorId : undefined}
            className={authInputClass}
          />
        </AuthField>
        <AuthField label="Repita a nova senha" htmlFor="confirm-password">
          <input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => {
              setConfirm(e.target.value);
            }}
            aria-invalid={mismatch || undefined}
            aria-describedby={error ? errorId : undefined}
            className={authInputClass}
          />
        </AuthField>
        {error && (
          <AuthNotice id={errorId} tone="error">
            {error}
          </AuthNotice>
        )}
        <AuthSubmit
          busy={busy}
          disabled={!password || !confirm}
          label="Salvar nova senha"
          busyLabel="Salvando…"
        />
      </form>
    </AuthLayout>
  );
}
